import React, { FunctionComponent, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { getCountries } from 'react-phone-number-input';
import countryLabels from 'react-phone-number-input/locale/en.json';
import { US_STATES } from '../../../../../utils/form/usStates.util';
import { FormTextInput } from '../../../common/inputs/TextInput';
import { SelectInput } from '../../../common/inputs/SelectInput';

const requiredAddress = { required: 'Address is required' };
const requiredCity = { required: 'City is required' };
const requiredState = { required: 'State is required' };
const requiredZipCode = { required: 'Zip code is required' };
const requiredCountry = { required: 'Country is required' };

export type BillingSelectOption = { value: string; label: string };

const COUNTRY_OPTIONS: BillingSelectOption[] = getCountries()
  .map((code) => ({ value: code, label: countryLabels[code] }))
  .sort((left, right) => left.label.localeCompare(right.label));

const STATE_OPTIONS: BillingSelectOption[] = US_STATES.map(([code, name]) => ({ value: code, label: name }));

const countryCode = (value: string) => (value === 'United States' ? 'US' : value);

export const billingCountryOption = (code: string) =>
  COUNTRY_OPTIONS.find((option) => option.value === countryCode(code));

export const billingStateOption = (code: string) => STATE_OPTIONS.find((option) => option.value === code);

export const billingFieldValue = (field: string | BillingSelectOption | null | undefined) => {
  if (field && typeof field === 'object') {
    return field.value.trim();
  }
  return (field ?? '').trim();
};

type Props = {
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
};

export const BillingInfoFields: FunctionComponent<Props> = ({
  address = '',
  city = '',
  state = '',
  zipCode = '',
  country = '',
}) => {
  const { watch, setValue } = useFormContext();
  const watchedCountry = watch('country') as string | BillingSelectOption | undefined;
  const watchedState = watch('state') as string | BillingSelectOption | undefined;
  const selectedCountryCode = countryCode(billingFieldValue(watchedCountry) || country);
  const isUnitedStates = selectedCountryCode === 'US';
  const savedCountryCode = countryCode(country);
  const showSavedState = selectedCountryCode === savedCountryCode;
  const previousCountryCode = useRef(selectedCountryCode);

  useEffect(() => {
    if (previousCountryCode.current === selectedCountryCode) {
      return;
    }
    previousCountryCode.current = selectedCountryCode;
    setValue('state', '');
  }, [selectedCountryCode, setValue]);

  useEffect(() => {
    if (typeof watchedCountry === 'string' && watchedCountry !== '') {
      const match = billingCountryOption(watchedCountry);
      if (match) {
        setValue('country', match);
      }
    }
  }, [setValue, watchedCountry]);

  useEffect(() => {
    if (!isUnitedStates || typeof watchedState !== 'string' || watchedState === '') {
      return;
    }
    const match = billingStateOption(watchedState);
    if (match) {
      setValue('state', match);
    }
  }, [isUnitedStates, setValue, watchedState]);

  const countryDefault = COUNTRY_OPTIONS.find((option) => option.value === savedCountryCode);
  const stateDefault = showSavedState ? STATE_OPTIONS.find((option) => option.value === state) : undefined;

  return (
    <>
      <div className="col-12">
        <div className="field-container">
          <FormTextInput
            id="billing-address"
            name="address"
            type="text"
            label="Address"
            placeholder="Enter"
            defaultValue={address}
            validation={requiredAddress}
          />
        </div>
      </div>
      <div className="flex">
        <div className="col-6">
          <div className="field-container">
            <SelectInput
              id="billing-country"
              name="country"
              label="Country"
              placeholder="Select"
              options={COUNTRY_OPTIONS}
              defaultOptions={countryDefault}
              validation={requiredCountry}
              isSingle
            />
          </div>
        </div>
        <div className="col-6">
          <div className="field-container">
            {isUnitedStates ? (
              <SelectInput
                id="billing-state"
                name="state"
                label="State"
                placeholder="Select"
                options={STATE_OPTIONS}
                defaultOptions={stateDefault}
                validation={requiredState}
                isSingle
              />
            ) : (
              <FormTextInput
                id="billing-state"
                name="state"
                type="text"
                label="State"
                placeholder="Enter"
                defaultValue={showSavedState ? state : ''}
                validation={requiredState}
              />
            )}
          </div>
        </div>
      </div>
      <div className="flex">
        <div className="col-6">
          <div className="field-container">
            <FormTextInput
              id="billing-city"
              name="city"
              type="text"
              label="City"
              placeholder="Enter"
              defaultValue={city}
              validation={requiredCity}
            />
          </div>
        </div>
        <div className="col-6">
          <div className="field-container">
            <FormTextInput
              id="billing-zip-code"
              name="zipCode"
              type="text"
              label="Zip Code"
              placeholder="Enter"
              defaultValue={zipCode}
              validation={requiredZipCode}
            />
          </div>
        </div>
      </div>
    </>
  );
};
